import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import {
  generateDocumentForBeneficiary,
  saveBeneficiaryDocument
} from '../../services/beneficiaryDocumentService';
import './BeneficiaryDocumentGenerator.css';

/**
 * Composant pour générer des documents personnalisés pour un bénéficiaire
 */
const BeneficiaryDocumentGenerator = ({
  db,
  user,
  beneficiaryId = null, // ID du bénéficiaire pré-sélectionné (optionnel)
  onClose,
  onDocumentGenerated,
  onCancel
}) => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [additionalData, setAdditionalData] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Sélection, 2: Données additionnelles, 3: Prévisualisation
  const isEmbedded = !!beneficiaryId; // Si beneficiaryId fourni, c'est un mode intégré (sans overlay)

  // Types de documents disponibles
  const documentTypes = [
    { id: 'MANDAT', label: 'Contrat de Prestation de Mandat', requiresAdditionalData: true },
    { id: 'CGS', label: 'Conditions Générales de Service', requiresAdditionalData: false },
    { id: 'CONTRAT_TRAVAIL', label: 'Contrat de Travail', requiresAdditionalData: true },
    { id: 'LIVRET_ACCUEIL', label: 'Livret d\'Accueil', requiresAdditionalData: false },
    { id: 'REGLEMENT_BENEFICIAIRES', label: 'Règlement Intérieur Bénéficiaires', requiresAdditionalData: false },
    { id: 'ATTESTATION_FISCALE', label: 'Attestation Fiscale Annuelle', requiresAdditionalData: true },
    { id: 'CHARTE_DROITS', label: 'Charte des Droits et Libertés', requiresAdditionalData: false }
  ];

  // Charger la liste des bénéficiaires et pré-sélectionner si beneficiaryId fourni
  useEffect(() => {
    loadBeneficiaries();
  }, []);

  // Pré-sélectionner le bénéficiaire si l'ID est fourni
  useEffect(() => {
    console.log('[BeneficiaryDocumentGenerator] Pre-selection check:', {
      beneficiaryId,
      beneficiariesCount: beneficiaries.length,
      isEmbedded
    });
    if (beneficiaryId && beneficiaries.length > 0) {
      const beneficiary = beneficiaries.find(b => b.id === beneficiaryId);
      console.log('[BeneficiaryDocumentGenerator] Found beneficiary:', beneficiary);
      if (beneficiary) {
        setSelectedBeneficiary(beneficiary);
      }
    }
  }, [beneficiaryId, beneficiaries]);

  const loadBeneficiaries = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const snapshot = await getDocs(clientsRef);
      const beneficiariesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBeneficiaries(beneficiariesList);
    } catch (error) {
      console.error('Erreur lors du chargement des bénéficiaires:', error);
      setError('Impossible de charger la liste des bénéficiaires');
    }
  };

  // Passer à l'étape suivante
  const handleNext = () => {
    if (step === 1 && selectedBeneficiary && documentType) {
      const selectedDocType = documentTypes.find(dt => dt.id === documentType);
      if (selectedDocType?.requiresAdditionalData) {
        setStep(2);
      } else {
        generatePreview();
      }
    } else if (step === 2) {
      generatePreview();
    }
  };

  // Générer la prévisualisation
  const generatePreview = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      const documentData = await generateDocumentForBeneficiary(
        db,
        selectedBeneficiary.id,
        documentType,
        additionalData,
        user.uid
      );

      setPreview(documentData);
      setStep(3);
    } catch (error) {
      console.error('Erreur lors de la génération de la prévisualisation:', error);
      setError('Erreur lors de la génération du document: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Sauvegarder le document
  const handleSave = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      const documentId = await saveBeneficiaryDocument(
        db,
        selectedBeneficiary.id,
        preview
      );

      alert('Document généré et sauvegardé avec succès !');

      if (onDocumentGenerated) {
        onDocumentGenerated(documentId);
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du document:', error);
      setError('Erreur lors de la sauvegarde: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Gérer les données additionnelles selon le type de document
  const renderAdditionalDataForm = () => {
    switch (documentType) {
      case 'MANDAT':
      case 'CONTRAT_TRAVAIL':
        return (
          <div className="additional-data-form">
            <h3>Informations du contrat</h3>

            <div className="form-group">
              <label>Date de début du contrat</label>
              <input
                type="date"
                value={additionalData.contrat?.startDate || ''}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  contrat: { ...additionalData.contrat, startDate: e.target.value }
                })}
                className="form-control"
              />
            </div>

            {documentType === 'CONTRAT_TRAVAIL' && (
              <>
                <div className="form-group">
                  <label>Type de contrat</label>
                  <select
                    value={additionalData.contrat?.durationType || 'CDI'}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      contrat: { ...additionalData.contrat, durationType: e.target.value }
                    })}
                    className="form-control"
                  >
                    <option value="CDI">CDI - Contrat à Durée Indéterminée</option>
                    <option value="CDD">CDD - Contrat à Durée Déterminée</option>
                  </select>
                </div>

                {additionalData.contrat?.durationType === 'CDD' && (
                  <div className="form-group">
                    <label>Date de fin du contrat</label>
                    <input
                      type="date"
                      value={additionalData.contrat?.endDate || ''}
                      onChange={(e) => setAdditionalData({
                        ...additionalData,
                        contrat: { ...additionalData.contrat, endDate: e.target.value }
                      })}
                      className="form-control"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Nombre d'heures hebdomadaires</label>
                  <input
                    type="number"
                    value={additionalData.contrat?.weeklyHours || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      contrat: { ...additionalData.contrat, weeklyHours: e.target.value }
                    })}
                    className="form-control"
                    placeholder="Ex: 35"
                  />
                </div>

                <div className="form-group">
                  <label>Taux horaire brut (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={additionalData.contrat?.hourlyRateBrut || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      contrat: { ...additionalData.contrat, hourlyRateBrut: e.target.value }
                    })}
                    className="form-control"
                    placeholder="Ex: 12.50"
                  />
                </div>

                <div className="form-group">
                  <label>Taux horaire net (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={additionalData.contrat?.hourlyRateNet || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      contrat: { ...additionalData.contrat, hourlyRateNet: e.target.value }
                    })}
                    className="form-control"
                    placeholder="Ex: 10.00"
                  />
                </div>

                <div className="form-group">
                  <label>Période d'essai (semaines)</label>
                  <input
                    type="number"
                    value={additionalData.contrat?.trialPeriod || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      contrat: { ...additionalData.contrat, trialPeriod: e.target.value }
                    })}
                    className="form-control"
                    placeholder="Ex: 2"
                  />
                </div>
              </>
            )}
          </div>
        );

      case 'ATTESTATION_FISCALE':
        return (
          <div className="additional-data-form">
            <h3>Informations fiscales</h3>

            <div className="form-group">
              <label>Année fiscale</label>
              <input
                type="number"
                value={additionalData.periode || new Date().getFullYear() - 1}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  periode: e.target.value
                })}
                className="form-control"
                placeholder="Ex: 2024"
              />
            </div>

            <div className="form-group">
              <label>Nombre d'heures d'aide à la personne</label>
              <input
                type="number"
                value={additionalData.montants?.heuresAide || ''}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  montants: { ...additionalData.montants, heuresAide: e.target.value }
                })}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Montant aide à la personne (€)</label>
              <input
                type="number"
                step="0.01"
                value={additionalData.montants?.montantAide || ''}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  montants: { ...additionalData.montants, montantAide: e.target.value }
                })}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Montant total annuel (€)</label>
              <input
                type="number"
                step="0.01"
                value={additionalData.montants?.totalMontant || ''}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  montants: { ...additionalData.montants, totalMontant: e.target.value }
                })}
                className="form-control"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const content = (
    <>
      {/* Header (seulement si pas intégré) */}
      {!isEmbedded && (
        <div className="modal-header">
          <h2>
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Générer un document pour un bénéficiaire
          </h2>
          <button onClick={onClose} className="close-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

        {/* Stepper */}
        <div className="stepper">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Sélection</div>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Données</div>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Prévisualisation</div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="alert alert-error">
            <svg className="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Content */}
        <div className="modal-body">
          {step === 1 && (
            <div className="step-content">
              {/* Sélection du bénéficiaire (seulement si pas pré-sélectionné) */}
              {!isEmbedded && (
                <div className="form-group">
                  <label>Sélectionner un bénéficiaire *</label>
                  <select
                    value={selectedBeneficiary?.id || ''}
                    onChange={(e) => {
                      const beneficiary = beneficiaries.find(b => b.id === e.target.value);
                      setSelectedBeneficiary(beneficiary);
                    }}
                    className="form-control"
                  >
                    <option value="">-- Choisir un bénéficiaire --</option>
                    {beneficiaries.map(beneficiary => (
                      <option key={beneficiary.id} value={beneficiary.id}>
                        {beneficiary.firstName} {beneficiary.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedBeneficiary && (
                <div className="beneficiary-info-card">
                  <h4>Informations du bénéficiaire</h4>
                  <p><strong>Nom :</strong> {selectedBeneficiary.firstName} {selectedBeneficiary.lastName}</p>
                  <p><strong>Adresse :</strong> {(() => {
                    const adresse = selectedBeneficiary.address;
                    if (typeof adresse === 'object' && adresse !== null) {
                      const { street, city, postalCode, country } = adresse;
                      const parties = [];
                      if (street) parties.push(street);
                      if (postalCode) parties.push(postalCode);
                      if (city) parties.push(city);
                      if (country) parties.push(country);
                      return parties.join(', ') || 'Non renseignée';
                    }
                    return adresse || 'Non renseignée';
                  })()}</p>
                  <p><strong>Téléphone :</strong> {selectedBeneficiary.phone || 'Non renseigné'}</p>
                  <p><strong>Email :</strong> {selectedBeneficiary.email || 'Non renseigné'}</p>
                </div>
              )}

              <div className="form-group">
                <label>Type de document *</label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Choisir un type de document --</option>
                  {documentTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              {renderAdditionalDataForm()}
            </div>
          )}

          {step === 3 && preview && (
            <div className="step-content">
              <div className="preview-header">
                <h3>{preview.documentName}</h3>
                <span className="badge badge-info">Statut: {preview.status}</span>
              </div>
              <div className="document-preview" dangerouslySetInnerHTML={{ __html: preview.content }}></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-buttons">
            {step > 1 && step < 3 && (
              <button
                onClick={() => setStep(step - 1)}
                className="btn btn-secondary"
                disabled={isGenerating}
              >
                Retour
              </button>
            )}

            <button
              onClick={onCancel || onClose}
              className="btn btn-secondary"
              disabled={isGenerating}
            >
              Annuler
            </button>

            {step < 3 ? (
              <button
                onClick={handleNext}
                className="btn btn-primary"
                disabled={!selectedBeneficiary || !documentType || isGenerating}
              >
                {isGenerating ? 'Génération...' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={handleSave}
                className="btn btn-success"
                disabled={isGenerating}
              >
                {isGenerating ? 'Sauvegarde...' : 'Sauvegarder le document'}
              </button>
            )}
          </div>
        </div>
    </>
  );

  // Mode intégré (sans overlay) - utilisé dans un modal externe
  if (isEmbedded) {
    return (
      <div className="beneficiary-document-generator-embedded">
        {content}
      </div>
    );
  }

  // Mode standalone (avec overlay) - utilisé comme modal indépendant
  return (
    <div className="beneficiary-document-generator-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content large">
        {content}
      </div>
    </div>
  );
};

export default BeneficiaryDocumentGenerator;
